import { Button } from '@fox/atoms/src/button/index';
import { ConfirmPopover } from '@fox/atoms/src/popover/popovers';
import { DirtyState } from '@fox/atoms/src/dirty-state/index';
import { Icon } from '@fox/atoms/src/icon/index';
import { LoadingFox } from '@fox/atoms/src/animated-foxes/index';
import { PersistenceProvider, usePersistenceObjectUpdate } from '@fox/atoms/src/persistence-context/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { Wizard } from '@fox/atoms/src/wizard/wizard';
import { WizardValidation } from '@fox/atoms/src/wizard/hooks';
import { assertNever } from '@fox/atoms/utils/never';
import { processArrayInChunks } from '@fox/atoms/src/wizard/helpers';
import { useForceRender } from '@fox/atoms/src/popover/index';
import { CentredFlexRow, FlexColumnSpaced } from '@fox/layout/src/flex';
import React, { createContext, useContext, useReducer, useRef, useState } from 'react';
import { parseFile } from './parse-data';
import { checkRequiredFields, tryMatchColumns } from './sheet-utils';
import { ImportButtonLeft, ImportData } from './step-import-data';
import { MatchColumnsLargeStep } from './step-match-columns';
import { Review } from './step-review';
import { MatchOption, UpdateModifier, WorkBook, ImportProfile, CreateImportProfileInput, DeleteImportProfileInput } from './types';

export enum ValidationStatus {
	Errors,
	NotFound,
	Match
}

export enum ImportState {
	Idle,
	Validating,
	Importing,
	Complete
}

type OwnerRef = { ownerId: string; ownerType: string };

type PartialOrNull<T> = {
	[P in keyof T]?: T[P] | null;
};

export type ValidationResult<T, TMatch = unknown> = T & {
	result: ValidationStatus;
	matches?: TMatch[];
	errors?: Record<string, string>;
	selectedMatch?: TMatch | null;
	modifiers?: Array<UpdateModifier>;
	rowIndex?: number;
	rowAction?: string;
	canCreate?: boolean;
} & (OwnerRef | PartialOrNull<OwnerRef>);

export type ValidationResultOptionalResult<T, TMatch = unknown> = Omit<ValidationResult<T, TMatch>, 'result'> & {
	result?: ValidationStatus;
} & (OwnerRef | PartialOrNull<OwnerRef>);

export type ValidateFunc<T extends object, TValidated extends T = T, TMatch = unknown> = (
	chunk: T[],
	matchAlgorithm?: MatchOption,
	chunkIndex?: number,
	chunkSize?: number,
	custom?: string[]
) => Promise<ValidationResult<TValidated, TMatch>[]>;

type ImportGridData = undefined | { workbook: WorkBook; fileName: string; fileSize: number; hash: string };
export type ImportContextState<TImport extends object = {}, TValidated extends TImport = TImport, TMatch = unknown> = {
	fileData: ImportGridData;
	sheet?: string;
	data?: Array<ValidationResult<TValidated, TMatch>>;
	matchAlgorithm?: string;

	/** The keys of the match columns that we want the import to match by (only applied if matchAlgorithm = 'custom')  */
	customMatchKeys?: string[];
	error?: unknown;
};

type ImportActions<TImport extends object = {}, TValidated extends TImport = TImport, TMatch = unknown> =
	| ({ type: 'FILE' } & ImportGridData)
	| { type: 'CLEAR_FILE' }
	| { type: 'SELECT_SHEET'; name: string }
	| { type: 'PARSED'; data: Array<ValidationResult<TValidated, TMatch>> }
	| { type: 'UPDATE_ROW'; updated: ValidationResultOptionalResult<TValidated, TMatch>; original: ValidationResult<TValidated, TMatch> }
	| { type: 'SET_HAS_HEADER' }
	| { type: 'SET_SEARCH'; value: string; custom?: string[] }
	| { type: 'ERROR'; error: unknown }
	| { type: 'CLEAR_ERROR' };

export const INITIAL_STATE: ImportContextState = {
	fileData: undefined,
	error: undefined
};

export const importGridReducer = <TImport extends object, TValidated extends TImport = TImport, TMatch = unknown>() => (
	state: ImportContextState<TImport, TValidated, TMatch>,
	action: ImportActions<TImport, TValidated, TMatch>
): ImportContextState<TImport, TValidated, TMatch> => {
	switch (action.type) {
		case 'CLEAR_FILE':
			return {
				...INITIAL_STATE,
				matchAlgorithm: state?.matchAlgorithm
			} as ImportContextState<TImport, TValidated, TMatch>;
		case 'PARSED':
			return { ...state, data: action.data };
		case 'UPDATE_ROW':
			return { ...state, data: state?.data?.map(a => (a === action.original ? { ...a, ...action.updated } : a)) };
		case 'FILE':
			return {
				...state,
				data: undefined,
				fileData: { fileName: action.fileName, workbook: action.workbook, fileSize: action.fileSize, hash: action.hash },
				sheet: action?.workbook?.SheetNames?.[0]
			};
		case 'SELECT_SHEET':
			return { ...state, data: undefined, sheet: action.name };
		case 'SET_HAS_HEADER':
			return { ...state, data: undefined };
		case 'SET_SEARCH':
			return { ...state, data: undefined, matchAlgorithm: action.value, customMatchKeys: action.custom };
		case 'ERROR':
			return { ...state, error: action.error };
		case 'CLEAR_ERROR':
			return { ...state, error: undefined };
	}
	assertNever(action);
};

export type ReviewProps<T extends object, TMatch = unknown> = {
	data: Array<ValidationResult<T, TMatch>>;
	mappedColumns: Array<string>;
};

export type MapOption = Readonly<{
	title: string;
	key: string;
	grouping?: string;
	// elastic match key (if present should be searchable) - though we may need to have different operators
	match?: string;

	// do we want do disable searching of these?
	// searchable?: boolean;
}>;

type ImportWizardHook<
	TImport extends object,
	TValidated extends TImport = TImport,
	TMatch = unknown,
	TMapOption extends MapOption = MapOption,
	TBeginData = void
> = {
	mapTypes: readonly TMapOption[];
	performValidateChunk: ValidateFunc<TImport, TValidated, TMatch>;
	onBeginImport?: (
		state: ImportContextState<TImport, TValidated, TMatch>,
		mappings: Record<string, string>,
		profile?: ImportProfile
	) => Promise<TBeginData>;
	/**
	 * Custom label for importing status (default: `Importing ...`)
	 */
	importingLabel?: string;
	onFinishImport?: (
		state: ImportContextState<TImport, TValidated, TMatch>,
		mappings: Record<string, string>,
		begin: TBeginData
	) => Promise<void>;

	/** Format the data coming in - auto will be the raw data mapped to the expected columns, per map. You can explicitly type this argument as
	 *
	 * Record<typeof importMapOptions[number]['key'], string>
	 *
	 * If the importMapOptions is declared `as const`
	 */
	format: (
		auto: Record<TMapOption[][number]['key'], string>,
		data: Record<string, string>,
		map: Record<string, string>,
		custom?: string[]
	) => TImport;
	ReviewComponent: React.ComponentType<ReviewProps<TImport>>;
	importChunk: (data: Array<ValidationResult<TValidated, TMatch>>, chunkNo: number, beginData?: TBeginData) => Promise<boolean>;
	importOverview?: React.ComponentType<Pick<ReviewProps<TImport>, 'data'>>;
	profile?: ImportProfile;
	setProfile?: React.Dispatch<React.SetStateAction<ImportProfile>>;
	matchOptions: MatchOption[];
	category: string;
	/** @default 50 */
	chunkSize?: number;
	createImportProfile?: (input: CreateImportProfileInput) => Promise<void>;
	deleteImportProfile?: (input: DeleteImportProfileInput) => Promise<void>;
	getImportProfiles?: (category: string) => Promise<ImportProfile[]>;
	/**Custom import button to override the standard import button */
	customImportButton?: React.ComponentType<{
		state: ImportContextState<TImport, TValidated, TMatch>;
		importState: ImportState;
		onClick: () => void;
	}>;
};

export type ImportWizardProps<
	TImport extends object,
	TValidated extends TImport = TImport,
	TMatch = unknown,
	TMapOption extends MapOption = MapOption,
	TBeginData = void
> = {
	/** An element that will be rendered before the file drop component, on the first step of the importer wizard.
	 * This can be used to show some guidelines, or perhaps some additional parameters for the import */
	guidelines?: JSX.Element;
} & ImportWizardHook<TImport, TValidated, TMatch, TMapOption, TBeginData>;

export type ImportContextValue<
	TImport extends object = {},
	TValidated extends TImport = TImport,
	TMatch = unknown,
	TMapOption extends MapOption = MapOption,
	TBeginData = void
> = {
	state: ImportContextState<TImport, TValidated, TMatch>;
	dispatch: React.Dispatch<ImportActions<TImport>>;
	mapRef: Record<string, string>;
	setMapping: (key: string, value: string) => void;
	importState: ImportState;
	setImportState: React.Dispatch<React.SetStateAction<ImportState>>;
	onDrop: (file: File[]) => Promise<void>;

	profile?: ImportProfile;
	setProfile?: React.Dispatch<React.SetStateAction<ImportProfile>>;
} & ImportWizardProps<TImport, TValidated, TMatch, TMapOption, TBeginData>;

const Context = createContext<ImportContextValue | undefined>(undefined);
Context.displayName = 'ImportContext';
export const useImportWizardContext = <
	TImport extends object,
	TValidated extends TImport = TImport,
	TMatch = unknown,
	TMapOption extends MapOption = MapOption,
	TBeginData = void
>() => useContext(Context) as ImportContextValue<TImport, TValidated, TMatch, TMapOption, TBeginData>;

export const DEFAULT_IMPORTER_PROFILE: ImportProfile = { id: '', hasHeader: true, mapping: null, name: null };

export function useImporter<
	TImport extends object = {},
	TValidated extends TImport = TImport,
	TMatch = unknown,
	TMapOption extends MapOption = MapOption,
	TBeginData = void
>(opts: ImportWizardHook<TImport, TValidated, TMatch, TMapOption, TBeginData>) {
	const { mapTypes, matchOptions } = opts;
	const reducer = importGridReducer<TImport, TValidated, TMatch>();
	const matchAlgorithmId = matchOptions.find(o => o.default)?.id ?? matchOptions?.[0]?.id;

	const [state, dispatch] = useReducer(reducer, {
		...INITIAL_STATE,
		matchAlgorithm: matchAlgorithmId
	} as ImportContextState<TImport, TValidated, TMatch>);

	const rerender = useForceRender();
	const mapRef = useRef<Record<string, string>>({});

	const [profile, setProfile] = useState<ImportProfile>(DEFAULT_IMPORTER_PROFILE);

	const [importState, setImportState] = useState(ImportState.Idle);

	function setMapping(key: string, value: string) {
		if (!value) {
			delete mapRef.current[key];
		} else {
			mapRef.current[key] = value;
		}

		rerender();
	}

	async function onDrop(e: File[]) {
		const first = e[0];

		const workbook = await parseFile(first);

		dispatch({ type: 'FILE', workbook, fileName: first.name, fileSize: first.size, hash: workbook.fileHash });

		const sheet = workbook?.SheetNames?.[0];
		const ws = workbook?.GetSheet(sheet);
		const header = ws.extractHeader(profile?.hasHeader);
		tryMatchColumns(header, setMapping, mapTypes, mapRef.current);
	}

	const T = Context as React.Context<ImportContextValue<TImport, TValidated, TMatch, TMapOption, TBeginData>>;

	const contextValue = {
		...opts,
		state,
		onDrop,
		dispatch,
		mapRef: mapRef.current,
		setMapping,
		profile,
		setProfile,
		importState,
		setImportState
	};

	return { contextValue, ImportProvider: T.Provider };
}

/**
The Import Wizard is a component that facilitates client side upload and mapping of data using modern browser capability.

The goals are to
1. Accept a file upload of structured data (csv, xlsx)
2. Parse it into a normalised structure in memory
3. Display the data on an AutoDataGrid grid with header components allowing selecting a possible output column
4. Preview the parsed/normalised results
5. Perform the import

TODO

We need to support a date format parsing setting, some files could be D-M-Y, could be M-D-Y and may or may not have time specifier.
I'd like to implement the lock prop to wizards, preventing changing of steps during the critical state.

*/

// a very theoretical fix to the persistence context needing to unmount upon clicking import.
export function ImportButton<TImport extends object = {}, TValidated extends TImport = TImport, TMatch = unknown>({
	state,
	importState,
	onClick
}: {
	state: ImportContextState<TImport, TValidated, TMatch>;
	importState: ImportState;
	onClick: () => void;
}) {
	const { updateData } = usePersistenceObjectUpdate();
	return (
		<Button
			color="primary"
			disabled={importState !== ImportState.Idle || state?.data?.length < 1}
			onClick={() => {
				onClick();
				updateData(null);
			}}
		>
			Import
		</Button>
	);
}

export function BasicImportWizard<
	TImport extends object = {},
	TValidated extends TImport = TImport,
	TMatch = unknown,
	TMapOption extends MapOption = MapOption,
	TBeginData = void
>({
	onBeginImport,
	onFinishImport,
	importingLabel,
	importOverview: ImportOverview,
	chunkSize = 50,
	guidelines,
	createImportProfile,
	deleteImportProfile,
	getImportProfiles,
	customImportButton: CustomImportButton,
	...opts
}: ImportWizardProps<TImport, TValidated, TMatch, TMapOption, TBeginData>) {
	const { contextValue, ImportProvider } = useImporter<TImport, TValidated, TMatch, TMapOption, TBeginData>({
		...opts,
		chunkSize,
		createImportProfile,
		deleteImportProfile,
		getImportProfiles
	});

	const { state, importState, setImportState, mapRef, importChunk, matchOptions, profile } = contextValue;

	const [sent, setSent] = useState(0);

	const importData = async () => {
		setImportState(ImportState.Importing);

		const beginData = await onBeginImport?.(state, mapRef, profile);

		await processArrayInChunks(state.data, chunkSize, async (chunk, chunkNo) => {
			await importChunk(chunk, chunkNo, beginData);
			setSent(p => p + 1);
		});

		setImportState(ImportState.Complete);
		await onFinishImport?.(state, mapRef, beginData);
	};

	if (importState === ImportState.Importing) {
		const percent = Math.min(1, Math.max((sent * chunkSize) / (state.data?.length ?? 1), 0));
		return (
			<div style={{ height: '100%', display: 'grid', placeContent: 'center' }}>
				<FlexColumnSpaced>
					<DirtyState
						dirty={importState === ImportState.Importing}
						message="Importing is proceeding, unable to leave this page"
					/>
					<CentredFlexRow>
						<LoadingFox loading={true} height="5rem" />
					</CentredFlexRow>
					<Typography variant="body1">
						{`${importingLabel ?? 'Importing...'} ${percent.toLocaleString(undefined, {
							style: 'percent',
							minimumFractionDigits: 0
						})}`}
					</Typography>
				</FlexColumnSpaced>
			</div>
		);
	}

	if (importState === ImportState.Complete) {
		return ImportOverview ? (
			<ImportOverview data={state.data} />
		) : (
			<CentredFlexRow>
				<Icon iconName="CheckCircle" fontSize="large" color={'success'} /> Import complete
			</CentredFlexRow>
		);
	}

	return (
		<ImportProvider value={contextValue}>
			<PersistenceProvider>
				<Wizard
					lock={importState !== ImportState.Idle}
					steps={[
						{
							title: 'Import data',
							component: () => <ImportData guidelines={guidelines} />,
							validation: () => (!!state.fileData ? WizardValidation.Valid : WizardValidation.Disabled),
							buttonsLeft: ImportButtonLeft
						},
						{
							title: 'Match columns',
							component: MatchColumnsLargeStep,
							validation: () => (!!state.fileData ? WizardValidation.Valid : WizardValidation.Disabled),
							buttonsRight: props => {
								const algo = matchOptions?.find(o => o.id === state?.matchAlgorithm)?.requiredFields;

								const isValid = !algo ? true : checkRequiredFields(algo, mapRef);
								return algo?.length > 0 ? (
									<ConfirmPopover
										disabled={!isValid}
										confirm={() => props.handleNextStep()}
										message="This process may take some time to complete, do you wish to continue?"
									>
										<Button variant="primary" disabled={!isValid}>
											Next
										</Button>
									</ConfirmPopover>
								) : (
									<Button variant="primary" disabled={!isValid} onClick={props.handleNextStep}>
										Next
									</Button>
								);
							}
						},
						{
							title: 'Review',
							component: Review,
							validation: () => {
								const algo = matchOptions?.find(o => o.id === state?.matchAlgorithm)?.requiredFields;

								if (!algo) {
									return WizardValidation.Valid;
								}
								const isValid = checkRequiredFields(algo, mapRef);

								return isValid ? WizardValidation.Valid : WizardValidation.Disabled;
							},
							buttonsRight: () => CustomImportButton? <CustomImportButton state={state} importState={importState} onClick={importData}/> : <ImportButton state={state} importState={importState} onClick={importData} />
						}
					]}
				/>
			</PersistenceProvider>
		</ImportProvider>
	);
}
